export type Product = (typeof Product.List)[number];
export namespace Product {
  export type Key = Product['key'];
  export type Plan<P extends Key> = Extract<Product, { key: P }>['plans'][number]['key'];

  export type Base = {
    key: string;
    name: string;
    path: string;
    plans: { key: string; name: string }[];
    /**
     * 提供開始前で、プランを有効にしても実体が動かないプロダクトに設定する。
     *
     * プロダクト利用設定の一覧は Product.List を無条件に列挙するため、開発中の
     * プロダクトも管理者には見えてしまう。提供中のものと見分けがつかないと
     * 誤って有効化されるので、UI 側で注意書きを出すための目印として持たせている。
     *
     * 提供を開始したらこのフィールドごと消すこと。
     */
    unavailableNote?: string;
  };

  export const Knowledge = {
    key: 'knowledge',
    name: 'ナレッジハブ',
    path: '/knowledge/',
    plans: [
      { key: 'none', name: '利用なし' },
      { key: 'enterprise', name: 'エンタープライズ' },
    ],
  } as const satisfies Base;
  export type Knowledge = typeof Knowledge;

  export const Desk = {
    key: 'desk',
    name: 'ナレッジデスク',
    path: '/desk/',
    plans: [
      { key: 'none', name: '利用なし' },
      { key: 'enterprise', name: 'エンタープライズ' },
    ],
  } as const satisfies Base;
  export type Desk = typeof Desk;

  export const Neuro = {
    key: 'neuro',
    name: 'AIオペレーター',
    path: '/neuro/',
    plans: [
      { key: 'none', name: '利用なし' },
      { key: 'enterprise', name: 'エンタープライズ' },
    ],
  } as const satisfies Base;
  export type Neuro = typeof Neuro;

  export const Troppus = {
    key: 'troppus',
    name: '顧客データベース',
    path: '/troppus/',
    plans: [
      { key: 'none', name: '利用なし' },
      { key: 'enterprise', name: 'エンタープライズ' },
    ],
  } as const satisfies Base;
  export type Troppus = typeof Troppus;

  export const Voc = {
    key: 'voc',
    name: 'VoC',
    path: '/voc/',
    plans: [
      { key: 'none', name: '利用なし' },
      { key: 'enterprise', name: 'エンタープライズ' },
    ],
  } as const satisfies Base;
  export type Voc = typeof Voc;

  export const Speak = {
    key: 'speak',
    name: 'Speak',
    path: '/speak/',
    plans: [
      { key: 'none', name: '利用なし' },
      { key: 'enterprise', name: 'エンタープライズ' },
    ],
  } as const satisfies Base;
  export type Speak = typeof Speak;

  export const Debeso = {
    key: 'debeso',
    name: '外部連携機能',
    path: '/debeso/',
    plans: [
      { key: 'none', name: '利用なし' },
      { key: 'enterprise', name: 'エンタープライズ' },
    ],
  } as const satisfies Base;
  export type Debeso = typeof Debeso;

  export const SmartFaq = {
    key: 'smartFaq',
    name: 'スマートFAQ',
    path: '/smart-faq/',
    plans: [
      { key: 'none', name: '利用なし' },
      { key: 'enterprise', name: 'エンタープライズ' },
    ],
    unavailableNote: '開発中のため、有効にしても利用できません',
  } as const satisfies Base;
  export type SmartFaq = typeof SmartFaq;

  export const Tabula = {
    key: 'tabula',
    name: 'リソーステーブル',
    path: '/tabula/',
    plans: [
      { key: 'none', name: '利用なし' },
      { key: 'enterprise', name: 'エンタープライズ' },
    ],
  } as const satisfies Base;
  export type Tabula = typeof Tabula;

  /**
   * 提供開始前のプロダクトなら、その注意書きを返す。提供中なら undefined。
   *
   * `unavailableNote` は Base では任意だが、各プロダクトは `as const satisfies Base` で
   * 定義されているため持たないものの型にはキー自体が無く、union 越しに直接は読めない。
   */
  export const UnavailableNote = (product: Product): string | undefined =>
    'unavailableNote' in product ? product.unavailableNote : undefined;

  export const List = [Knowledge, Desk, Neuro, Troppus, Voc, Speak, Debeso, SmartFaq, Tabula] as const;
  export const Map = Object.fromEntries(Product.List.map((product) => [product.key, product])) as {
    [K in Product.Key]: Extract<Product, { key: K }>;
  };
}
